import React, { useState } from "react"
import cx from "classnames"
import { useDispatch } from "react-redux"
import { useDropzone } from "react-dropzone"

import { uploadImage } from "@/store/features/genericSlice"
import closeIcon from "@/public/icons/close.svg"
import PdfIcon from "@/public/icons/pdf-grey.svg"
import pageLoader from "@/public/icons/page-loader.svg"

const MAX_FILES = 5
const MAX_FILE_SIZE = 5e6

const UploadProjectFiles = ({
  imageList = [],
  updateList = () => {},
  i18n = {},
  instructionsId,
  onInteraction = () => {},
  onUploadInitiated = () => {},
  onUploadSuccess = () => {},
  onUploadError = () => {},
}) => {
  const dispatch = useDispatch()
  const [errors, setErrors] = useState([])
  const [status, setStatus] = useState("")
  const [isUploading, setIsUploading] = useState(false)

  const validator = file => {
    if (file?.size > MAX_FILE_SIZE) {
      return { message: i18n.errorMaxFileSizeLimit }
    }

    if (
      !["image/jpeg", "image/png", "image/gif", "application/pdf"].includes(
        file?.type
      )
    ) {
      return { message: i18n.errorFileType }
    }

    return null
  }

  const onDrop = async (acceptedFiles, rejectedFiles) => {
    onInteraction()
    onUploadInitiated()
    const selectedFileCount = acceptedFiles.length + rejectedFiles.length

    if (imageList.length + selectedFileCount > MAX_FILES) {
      setErrors([i18n.uploadCountLimit])
      onUploadError(i18n.uploadCountLimit)
      return
    }

    if (rejectedFiles.length) {
      const rejectedFile = rejectedFiles[0]
      const errorMessage =
        rejectedFile.file?.size > MAX_FILE_SIZE
          ? i18n.errorMaxFileSizeLimit
          : rejectedFile.errors?.[0]?.code === "file-invalid-type"
          ? i18n.errorFileType
          : rejectedFile.errors?.[0]?.message

      setErrors([errorMessage].filter(Boolean))
      onUploadError(errorMessage)
      return
    }

    if (!acceptedFiles.length) return

    setErrors([])
    setStatus("")
    setIsUploading(true)

    try {
      const uploadedFiles = await Promise.all(
        acceptedFiles.map(async file => {
          const response = await dispatch(uploadImage(file)).unwrap()
          return Object.assign(file, {
            preview: URL.createObjectURL(file),
            link: response?.path,
          })
        })
      )
      const updatedFiles = [...imageList, ...uploadedFiles]
      updateList(updatedFiles)
      onUploadSuccess()
      setStatus(
        `${uploadedFiles.length} ${i18n.filesUploaded ?? "files uploaded"}. ${
          updatedFiles.length
        } of ${MAX_FILES}.`
      )
    } catch (error) {
      const errorMessage =
        i18n.uploadFailed ??
        "The selected files could not be uploaded. Please try again."
      setErrors([errorMessage])
      onUploadError(errorMessage)
    } finally {
      setIsUploading(false)
    }
  }

  const removeFile = file => {
    if (file?.preview) URL.revokeObjectURL(file.preview)
    const updatedFiles = imageList.filter(item => item !== file)
    updateList(updatedFiles)
    setStatus(
      `${file?.name} ${i18n.fileRemoved ?? "removed"}. ${
        updatedFiles.length
      } of ${MAX_FILES}.`
    )
  }

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/png, image/jpg, image/jpeg, image/gif, application/pdf",
    maxFiles: MAX_FILES,
    multiple: true,
    onDrop,
    validator,
    disabled: isUploading,
  })

  return (
    <div className="project-upload">
      {imageList.length ? (
        <div
          className="project-upload__file-list"
          role="list"
          aria-label={`${imageList.length} ${
            i18n.filesUploaded ?? "files uploaded"
          }`}
        >
          {imageList.map((file, index) => (
            <div
              className="project-upload__file"
              key={`${file?.name}-${index}`}
              role="listitem"
            >
              <div className="project-upload__preview">
                <img
                  className={cx({
                    "project-upload__preview-image":
                      file?.type !== "application/pdf",
                  })}
                  src={
                    file?.type === "application/pdf"
                      ? PdfIcon?.src
                      : file?.preview
                  }
                  alt=""
                  aria-hidden="true"
                />
              </div>
              <span className="sr-only">{file?.name}</span>
              <button
                type="button"
                className="project-upload__remove"
                onClick={() => removeFile(file)}
                aria-label={`${i18n.removeFile ?? "Remove"} ${file?.name}`}
              >
                <img src={closeIcon?.src} alt="" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {imageList.length < MAX_FILES ? (
        <>
          <input {...getInputProps()} />
          <div
            className="project-upload__dropzone"
            aria-busy={isUploading}
            {...getRootProps({
              role: "button",
              "aria-label": i18n.uploadFileButton,
              "aria-describedby": instructionsId,
            })}
          >
            {isUploading ? (
              <div
                className="project-upload__loader"
                role="status"
                aria-label={i18n.uploadingFiles ?? "Uploading files"}
              >
                <img src={pageLoader?.src} alt="" aria-hidden="true" />
              </div>
            ) : (
              <>
                <div className="project-upload__drop-title">
                  {i18n.dragAndDrop}
                </div>
                <div className="project-upload__drop-or">{i18n.orText}</div>
                <span className="project-upload__button" aria-hidden="true">
                  {i18n.uploadFileButton}
                </span>
              </>
            )}
          </div>
        </>
      ) : null}

      <div
        className="aria-messages"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {status}
      </div>
      {errors.length ? (
        <div
          className="project-upload__error"
          role="alert"
          aria-live="assertive"
        >
          {errors[0]}
        </div>
      ) : null}
    </div>
  )
}

export default UploadProjectFiles
